import React, { useEffect, useState } from 'react';

type ImageStatus = 'loading' | 'loaded' | 'failed';

export interface LomavisLazyImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'loading' | 'src'> {
  src?: string | null;
  containerClassName?: string;
  fallback?: React.ReactNode;
  loadingLabel: string;
}

/**
 * Atom: a lazy-loaded image with a light Bootstrap skeleton and an error
 * fallback. The parent owns the image dimensions through its container.
 */
export const LomavisLazyImage: React.FC<LomavisLazyImageProps> = ({
  src,
  alt = '',
  className = '',
  containerClassName = '',
  fallback,
  loadingLabel,
  onError,
  onLoad,
  ...imageProps
}) => {
  const [status, setStatus] = useState<ImageStatus>(src ? 'loading' : 'failed');

  useEffect(() => {
    setStatus(src ? 'loading' : 'failed');
  }, [src]);

  return (
    <div
      className={`position-relative w-100 h-100 overflow-hidden ${containerClassName}`}
    >
      {src && status !== 'failed' ? (
        <img
          {...imageProps}
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={`w-100 h-100 ${className} ${
            status === 'loading' ? 'invisible' : ''
          }`}
          onLoad={(event) => {
            setStatus('loaded');
            onLoad?.(event);
          }}
          onError={(event) => {
            setStatus('failed');
            onError?.(event);
          }}
        />
      ) : (
        fallback ?? (
          <div className="d-flex w-100 h-100 align-items-center justify-content-center bg-light text-muted">
            <i className="fa-regular fa-image fs-1" aria-hidden="true" />
          </div>
        )
      )}

      {src && status === 'loading' && (
        <div
          className="placeholder-wave position-absolute top-0 start-0 w-100 h-100 bg-light"
          role="status"
          aria-label={loadingLabel}
          data-testid="lazy-image-skeleton"
        >
          <span className="placeholder w-100 h-100 bg-secondary bg-opacity-10" />
        </div>
      )}
    </div>
  );
};
